/**
 * Comprehensive error handling utilities for the RGF Back Office application
 * Provides consistent error handling, classification, and user-friendly messaging
 */

export interface ValidationError {
  email_id?: string;
  phone_number?: string;
  error: string;
}

export interface ApiValidationErrorResponse {
  valid: false;
  message: string;
  error?: ValidationError[];
}

export interface ApiErrorResponse {
  message?: string;
  status?: number;
  data?: any;
}

export interface EnhancedError extends Error {
  isValidationError?: boolean;
  validationData?: ApiValidationErrorResponse;
  isNetworkError?: boolean;
  isAuthError?: boolean;
  statusCode?: number;
  originalError?: any;
}

/**
 * Error types for classification
 */
export enum ErrorType {
  VALIDATION = 'validation',
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  SERVER = 'server',
  UNKNOWN = 'unknown'
}

/**
 * Error classification based on error properties
 */
export function classifyError(error: any): ErrorType {
  // Check for validation errors
  if (error.isValidationError || error.response?.data?.valid === false) {
    return ErrorType.VALIDATION;
  }
  
  // Check for authentication errors
  if (error.response?.status === 401 || error.isAuthError) {
    return ErrorType.AUTHENTICATION;
  }
  
  // Check for authorization errors
  if (error.response?.status === 403) {
    return ErrorType.AUTHORIZATION;
  }
  
  // Check for network errors
  if (!error.response || error.code === 'NETWORK_ERROR' || error.isNetworkError) {
    return ErrorType.NETWORK;
  }
  
  // Check for server errors
  if (error.response?.status && error.response.status >= 500) {
    return ErrorType.SERVER;
  }
  
  return ErrorType.UNKNOWN;
}

/**
 * Get user-friendly error message based on error type and details
 */
export function getUserFriendlyErrorMessage(error: any): string {
  const errorType = classifyError(error);
  
  switch (errorType) {
    case ErrorType.VALIDATION:
      return error.response?.data?.message || error.message || 'Please check your input and try again';
    
    case ErrorType.AUTHENTICATION:
      return 'Your session has expired. Please log in again';
    
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action';
    
    case ErrorType.NETWORK:
      return 'Network error. Please check your connection and try again';
    
    case ErrorType.SERVER:
      return 'Server error. Please try again later or contact support';
    
    default:
      return error.response?.data?.message || error.message || 'An unexpected error occurred';
  }
}

/**
 * Enhanced error processing for API calls
 * Converts raw API errors into structured EnhancedError objects
 */
export function processApiError(error: any, context?: string): EnhancedError {
  const enhancedError: EnhancedError = new Error();
  
  // Preserve original error
  enhancedError.originalError = error;
  
  // Set error message
  enhancedError.message = getUserFriendlyErrorMessage(error);
  
  // Set status code
  enhancedError.statusCode = error.response?.status;
  
  // Classify and set error flags
  const errorType = classifyError(error);
  
  switch (errorType) {
    case ErrorType.VALIDATION:
      enhancedError.isValidationError = true;
      enhancedError.validationData = error.response?.data;
      break;
    
    case ErrorType.NETWORK:
      enhancedError.isNetworkError = true;
      break;
    
    case ErrorType.AUTHENTICATION:
      enhancedError.isAuthError = true;
      break;
  }
  
  // Add context if provided
  if (context) {
    enhancedError.message = `${context}: ${enhancedError.message}`;
  }
  
  return enhancedError;
}

/**
 * Log error with appropriate level based on error type
 */
export function logError(error: any, context?: string): void {
  const errorType = classifyError(error);
  const logContext = context ? `[${context}]` : '';
  
  switch (errorType) {
    case ErrorType.VALIDATION:
      console.warn(`${logContext} Validation Error:`, error);
      break;
    
    case ErrorType.AUTHENTICATION:
    case ErrorType.AUTHORIZATION:
      console.info(`${logContext} Auth Error:`, error);
      break;
    
    case ErrorType.NETWORK:
      console.warn(`${logContext} Network Error:`, error);
      break;
    
    case ErrorType.SERVER:
      console.error(`${logContext} Server Error:`, error);
      break;
    
    default:
      console.error(`${logContext} Unknown Error:`, error);
  }
  
  // Always log response details in development
  if (process.env.NODE_ENV === 'development' && error.response) {
    console.group(`${logContext} Error Details`);
    console.log('Status:', error.response.status);
    console.log('Data:', error.response.data);
    console.log('Headers:', error.response.headers);
    console.groupEnd();
  }
}

/**
 * Standard error handler for service methods
 * Use this in service catch blocks for consistent error handling
 */
export function handleServiceError(error: any, operation: string): never {
  const enhancedError = processApiError(error, operation);
  logError(error, operation);
  throw enhancedError;
}

/**
 * Error boundary helper for React components
 */
export function formatErrorForDisplay(error: any): {
  title: string;
  message: string;
  details?: any;
  canRetry: boolean;
} {
  const errorType = classifyError(error);
  
  let title: string;
  let canRetry = true;
  
  switch (errorType) {
    case ErrorType.VALIDATION:
      title = 'Input Validation Error';
      canRetry = false;
      break;
    
    case ErrorType.AUTHENTICATION:
      title = 'Authentication Required';
      canRetry = false;
      break;
    
    case ErrorType.AUTHORIZATION:
      title = 'Access Denied';
      canRetry = false;
      break;
    
    case ErrorType.NETWORK:
      title = 'Connection Error';
      canRetry = true;
      break;
    
    case ErrorType.SERVER:
      title = 'Server Error';
      canRetry = true;
      break;
    
    default:
      title = 'Unexpected Error';
      canRetry = true;
  }
  
  return {
    title,
    message: getUserFriendlyErrorMessage(error),
    details: error.isValidationError ? error.validationData : undefined,
    canRetry
  };
}

/**
 * Retry-safe error handler for network operations
 */
export function shouldRetryOperation(error: any, currentAttempt: number, maxAttempts: number): boolean {
  if (currentAttempt >= maxAttempts) {
    return false;
  }
  
  const errorType = classifyError(error);
  
  // Only retry network errors and server errors
  return errorType === ErrorType.NETWORK || errorType === ErrorType.SERVER;
}

/**
 * Get appropriate retry delay based on attempt number
 */
export function getRetryDelay(attempt: number, baseDelay = 1000): number {
  // Exponential backoff with jitter
  const delay = baseDelay * Math.pow(2, attempt - 1);
  const jitter = Math.random() * 0.1 * delay;
  return delay + jitter;
}